import { create } from 'zustand';

/**
 * Alarmes globais — a falha para de se esconder no meio da tela.
 *
 * Antes: cada tela escrevia o erro em texto vermelho no seu canto. Quem estava
 * noutra aba do sistema não via que o crédito da IA acabou ou que a API do
 * WordPress caiu — descobria pela matéria que não saiu. Agora toda falha sobe
 * para o canto superior direito, venha de onde vier.
 *
 * Erros de API entram sozinhos (o `apiFetch` reporta), então nenhuma tela nova
 * precisa lembrar de fazer isso.
 */

export type NivelAlerta = 'erro' | 'aviso' | 'ok';

export interface Alerta {
  id: number;
  nivel: NivelAlerta;
  titulo: string;
  detalhe?: string;
  /** Quantas vezes a MESMA falha repetiu (evita 20 toasts iguais numa rajada). */
  repeticoes: number;
  em: number;
  /** Erro persiste até o operador fechar; aviso/ok somem sozinhos. */
  fixo: boolean;
}

interface AlertasState {
  alertas: Alerta[];
  push: (a: Omit<Alerta, 'id' | 'em' | 'repeticoes' | 'fixo'> & { fixo?: boolean }) => void;
  fechar: (id: number) => void;
  limpar: () => void;
}

let seq = 1;
const TTL_MS = 7000;
/** Janela em que uma falha idêntica é contada em vez de empilhada. */
const JANELA_AGRUPAMENTO_MS = 30_000;
const MAX_VISIVEIS = 4;

export const useAlertas = create<AlertasState>((set, get) => ({
  alertas: [],
  push: ({ nivel, titulo, detalhe, fixo }) => {
    const agora = Date.now();
    const existente = get().alertas.find(
      (a) => a.titulo === titulo && a.nivel === nivel && agora - a.em < JANELA_AGRUPAMENTO_MS,
    );
    if (existente) {
      set((s) => ({
        alertas: s.alertas.map((a) =>
          a.id === existente.id ? { ...a, repeticoes: a.repeticoes + 1, em: agora } : a,
        ),
      }));
      return;
    }
    const persistente = fixo ?? nivel === 'erro';
    const alerta: Alerta = {
      id: seq++,
      nivel,
      titulo,
      detalhe,
      repeticoes: 1,
      em: agora,
      fixo: persistente,
    };
    set((s) => ({ alertas: [...s.alertas, alerta].slice(-MAX_VISIVEIS) }));
    if (!persistente) {
      setTimeout(() => get().fechar(alerta.id), TTL_MS);
    }
  },
  fechar: (id) => set((s) => ({ alertas: s.alertas.filter((a) => a.id !== id) })),
  limpar: () => set({ alertas: [] }),
}));

/** Atalho para quem não quer importar o store inteiro. */
export const alertar = (
  nivel: NivelAlerta,
  titulo: string,
  detalhe?: string,
  fixo?: boolean,
) => useAlertas.getState().push({ nivel, titulo, detalhe, fixo });
